import React, { useState, useEffect, Fragment }  from 'react';
import { useParams, Link as RouterLink } from "react-router-dom"
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';
import PaginatedTable from 'app/components/tables/PaginatedTable';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

const statuses = ['Delivered', 'Opened', 'Clicked', 'Failed', 'Unsubscribed','Complained'];

const useFilterStyles = makeStyles(theme => ({
    subSection: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[200]
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.grey[200]
    },
    dialogSpacing: {
        padding: theme.spacing(1)
    },
    noSpacing: {
        padding: 0
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    noContentPadding: {
        padding: 0
    }
}));

const EmailFilterDialog = (props) => {

    const classes = useFilterStyles()

    const { open, onClose, onFilter } = props;

    const [ filters, setFilters ] = useState([]);
    const [ statusChecked, setStatusChecked ] = useState([]);

    const api = useApi()
    const { app } = useApp();

    // Get Functions ------------------------------------------

    const getFilters = () => {
        const newFilters = app.user.application_state.deliveries.filters;
        const activeFilters = [];

        if (newFilters.status && newFilters.status instanceof Array && newFilters.status.length > 0) {
            setStatusChecked(newFilters.status);
            activeFilters.push('status')
        }

        setFilters(activeFilters);
    }

    useEffect(() => {
        getFilters();
    }, []);

    // Handlers ------------------------------------

    const handleFilter = filter => {
        const newFilters = [...filters];
        const i = newFilters.indexOf(filter)

        if (i > -1) {
            newFilters.splice(i, 1)
        } else {
            newFilters.push(filter)
        }

        setFilters(newFilters);
    }

    const handleStatus = value => {
        const newChecked = [...statusChecked];
        const i = newChecked.indexOf(value)

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(value)
        }

        setStatusChecked(newChecked);
    }

    const handleClose = () => {
        onClose();
    }

    // Posters -----------------------------

    const postFilter = () => {

        const data = {}

        if (filters.indexOf('status') > -1 && statusChecked.length > 0) {
            data.status = statusChecked;
        }

        // console.log(data)

        onFilter(data);
        onClose();
    }

    const postClear = () => {
        setStatusChecked([]);
        setFilters([]);
        onFilter({});
        onClose();
    }

    return (
        <Dialog
            maxWidth="xs"
            open={open}
            onClose={handleClose}
            fullWidth
            aria-labelledby="filter-users"
            >
            <DialogTitle className={classes.dialogSpacing}>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <Button color="primary" onClick={postClear} variant="outlined">
                            Clear
                        </Button>
                    </Grid>
                    <Grid item>
                        Filters
                    </Grid>
                    <Grid item>
                        <Button color="primary" variant="contained" onClick={postFilter}>
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </DialogTitle>
            <Divider />
            <DialogContent className={classes.noContentPadding}>
                <FormControlLabel
                    className={classes.noMarginLeft}
                    control={
                        <Checkbox
                            checked={filters.indexOf('status') > -1}
                            onChange={() => handleFilter('status')}
                            name="status"
                            color="primary"
                        />
                    }
                    label="Status"
                />
                <Divider />
                <Collapse in={filters.indexOf('status') > -1}>
                    <FormGroup className={classes.subSection}>
                        {statuses.map(status => (
                            <FormControlLabel
                                className={classes.noMarginLeft}
                                key={status}
                                control={
                                    <Checkbox
                                        checked={(statusChecked.indexOf(status) > -1)}
                                        onChange={()=>handleStatus(status)}
                                        name={status}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={status}
                            />
                        ))}
                    </FormGroup>
                </Collapse>
            </DialogContent>
        </Dialog>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: 980,
        margin: '0 auto',
        minHeight: 460,
        position: 'relative',
        padding: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(1)
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    loadingContainer: {
        height: 400,
        position: 'relative'
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    avatar: {
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff'
    },
    avatarError: {
        backgroundColor: theme.palette.error.main,
        color: '#ffffff'
    },
    avatarSuccess: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff'
    },
    flexGrow: {
        flexGrow: 1
    },
    noBorder: {
        border: 'none'
    },
    buttonBar: {

    }
}));

const columns = [
    {
        id: 'user_name',
        align: 'left',
        disablePadding: true,
        label: 'User',
        type: 'link',
        link: '/admin/users/',
        link_id: 'user_id'
    },
    {
        id: 'email',
        align: 'left',
        disablePadding: true,
        label: 'Email',
    },
    {
        id: 'status',
        align: 'left',
        disablePadding: true,
        label: 'Status',
        type: 'chip',
        map: {
            'Clicked': 'chipWarning',
            'Opened': 'chipSuccess',
            'Delivered': 'chipDefault',
            'Failed': 'chipError',
            'Unsubscribed': 'chipError',
            'Complained': 'chipError',
        }
    },
    {
        id: 'message',
        align: 'left',
        disablePadding: true,
        label: 'Message',
        wrap: true
    },
    {
        id: 'created_at',
        align: 'left',
        disablePadding: false,
        label: 'When',
        type: 'date',
        format: 'ff',
        highlight: true
    },
];

export default function ViewEmail(props) {

    const [ email, setEmail ] = useState(null);

    const [ loading, setLoading ] = useState(false);

    const api = useApi();
    const { app } = useApp();

    const classes = useStyles();

    let { id } = useParams();

    // GETTERS ------------------------------------------------

    const getEmail = () => {

        setLoading(true);

        api.get(`/admin/email/${id}`)
            .then((res) => {
                setEmail({
                    ...res.email,
                    sent_at: DateTime.fromISO(res.email.sent_at),
                    created_at: DateTime.fromISO(res.email.created_at),
                    updated_at: DateTime.fromISO(res.email.updated_at),
                    deleted_at: DateTime.fromISO(res.email.deleted_at),
                })
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getEmail();
    }, []);

    // POSTERS ------------------------------------------------

   

    return (
        <form className={classes.container} noValidate autoComplete="off">
            <Loading isLoading={loading}/>
            {email &&
                <Fragment>
                    <Grid container
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={3}
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <ButtonGroup color="primary" aria-label="email quick actions" variant="contained">
                                <Button>Copy</Button>
                                <Button>Template</Button>
                                <Button>Archive</Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item>
                                    <Button
                                        to="/admin/emails"
                                        component={RouterLink}
                                        startIcon={<ChevronLeftIcon />}
                                        >
                                        Back to Emails
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        to={`/admin/emails/${id}`}
                                        component={RouterLink}
                                        className={classes.buttonBar}
                                        >
                                        Edit Email
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3} justify="space-between">
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Subject
                                </Typography>
                                <Typography component="div" variant="h6">
                                    {email.subject}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Sent
                                </Typography>
                                <Typography component="div" variant="h6">
                                    {email.sent_at.toLocaleString(DateTime.DATETIME_MED)}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Divider className={classes.divider}/>
                        <Grid container spacing={3} justify="space-between">
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Delivered
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.delivered}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Opened
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.opened}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Clicked
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.clicked}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Failed
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.failed}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Unsubscribed
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.unsubscribed}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography component="div" variant="subtitle2" color="textSecondary">
                                    Complained
                                </Typography>
                                <Typography component="div" variant="h6" align='right'>
                                    {email.complained}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                    <Paper>
                        <PaginatedTable
                            columns={columns}
                            stateKey="deliveries"
                            label="Deliveries"
                            tableUrl={`/deliveries/${email.id}`}
                            FilterDialog={<EmailFilterDialog />}
                        />
                    </Paper>
                </Fragment>
            }
        </form>
    )
}